import React, { useEffect, useState } from 'react';
import moment from 'moment';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import T from 'Components/Helpers/translate';
import ErrorBoundary from 'App/components/ErrorBoundary';
import ShadcnScope from 'App/shadcn/components/ShadcnScope';
import { cn } from 'App/shadcn/lib/utils';
import { Sheet, SheetClose, SheetContent, SheetTitle } from 'App/shadcn/ui/sheet';
import { ResultView } from 'App/shadcn/pages/ProspectingTools/Lookup/ResultView';
import { LINK, QueryCard, ResultSkeleton } from 'App/shadcn/pages/ProspectingTools/Lookup/LookupParts';
import WaterfallPanel, { CompanySourcesPanel } from 'App/shadcn/pages/ProspectingTools/Lookup/WaterfallPanel';
import {
	runLookup,
	withChannel,
	type LookupChannel,
	type LookupResult
} from 'App/shadcn/pages/ProspectingTools/Lookup/lookupApi';

/** The email up front; the phone, at 20 times the price, only when the rep asks for it (WEB-13365). */
const FIND: LookupChannel[] = ['email'];
const WITH_PHONE: LookupChannel[] = ['email', 'phoneNumber'];

const MESSAGE =
	'rounded-[8px] border border-dashed border-grey-5 bg-background p-5 text-center text-body-md text-grey-11';

/** `key` is the query and how many times the rep ran it again (WEB-14049). */
type Answer = { key: string; result: LookupResult | null };
type PhoneRun = 'ask' | 'finding' | 'asked';

type BodyProps = { query: string; onLookUp: (query: string) => void; onClose: () => void };

/** Runs the lookup for `query`, once per query: mounting is the rep's choice, never typing. */
const LookupBody = ({ query, onLookUp, onClose }: BodyProps) => {
	const [answer, setAnswer] = useState<Answer | null>(null);
	const [phone, setPhone] = useState<{ key: string; run: PhoneRun } | null>(null);
	// "Look up again" on a stored result (WEB-14049) runs this query once more, paying again.
	const [again, setAgain] = useState<{ query: string; times: number } | null>(null);
	const times = again?.query === query ? again.times : 0;
	const key = `${times}:${query}`;
	// Each run starts out offering the phone.
	const phoneRun = phone?.key === key ? phone.run : 'ask';
	const loading = answer?.key !== key;

	useEffect(() => {
		let cancelled = false;
		runLookup(query, FIND, times > 0)
			.then(result => {
				if (!cancelled) setAnswer({ key: `${times}:${query}`, result });
			})
			.catch((err: unknown) => {
				console.error('[Supersearch lookup] request failed', err);
				if (!cancelled) setAnswer({ key: `${times}:${query}`, result: null });
			});
		return () => {
			cancelled = true;
		};
	}, [query, times]);

	const result = answer?.result ?? null;
	// Folded into this run's answer only: a phone run outlived by a new query is dropped.
	const findPhone = () => {
		setPhone({ key, run: 'finding' });
		runLookup(query, ['phoneNumber'])
			.then(phoneResult => {
				setAnswer(current =>
					current?.key === key && current.result
						? { key, result: withChannel(current.result, phoneResult, 'phoneNumber') }
						: current
				);
				setPhone(current => (current?.key === key ? { key, run: 'asked' } : current));
			})
			.catch((err: unknown) => {
				console.error('[Supersearch lookup] phone request failed', err);
				setPhone(current => (current?.key === key ? { key, run: 'ask' } : current));
			});
	};
	const find = phoneRun === 'asked' ? WITH_PHONE : FIND;
	return (
		<div
			className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5"
			// The result view's links (Enrichment settings, the CRM company) navigate in this tab, and the
			// non-modal panel would stay open over the page they lead to.
			onClickCapture={event => {
				const link = (event.target as Element).closest('a[href]');
				if (link && link.getAttribute('target') !== '_blank') onClose();
			}}
		>
			{/* What was looked up, until a found result's own card names it (WEB-13771). */}
			{loading || result?.status !== 'found' ? <QueryCard query={query} loading={loading} /> : null}
			{loading && !result ? <ResultSkeleton /> : null}
			{answer && !result ? (
				<p role="alert" className={MESSAGE}>
					{T('prospectingTools.lookup.status.failed')}
				</p>
			) : null}
			{result?.storedAt ? (
				// The user's own lookup from the last 15 days, shown instead of paying again (WEB-14049).
				<p role="status" className="text-body-sm text-grey-11">
					{T('prospectingTools.lookup.stored', { date: moment(result.storedAt).format('L') })}{' '}
					<button type="button" className={LINK} onClick={() => setAgain({ query, times: times + 1 })}>
						{T('prospectingTools.lookup.lookUpAgain')}
					</button>
				</p>
			) : null}
			{result ? (
				<div className={cn('flex flex-col gap-4', loading ? 'opacity-60' : '')}>
					<ResultView result={result} onLookUp={onLookUp} phone={{ state: phoneRun, onFind: findPhone }} />
					{result.person ? (
						<WaterfallPanel run={{ result, find }} />
					) : result.company ? (
						<CompanySourcesPanel run={{ result, find }} />
					) : null}
				</div>
			) : null}
		</div>
	);
};

type Props = {
	/** What the rep chose to look up. Supersearch mounts the panel only once there is one. */
	query: string;
	/** A follow-up from a result card ("Look up the company", a decision maker). */
	onLookUp: (query: string) => void;
	onClose: () => void;
};

/**
 * Supersearch's Lookup result (WEB-13132): the Lookup tab's cards in a side panel. Portaled to the
 * body so nothing in the navbar's layout can clip it; it sits above the navbar, below modals.
 */
const SupersearchLookupPanel = ({ query, onLookUp, onClose }: Props) =>
	ReactDOM.createPortal(
		<ErrorBoundary>
			<ShadcnScope>
				<Sheet
					open
					// Not modal, like the AI Workspace's drawer: the modal scroll lock cannot see across
					// the shadow root and eats wheel scrolling inside the panel. Outside clicks still close it.
					modal={false}
					onOpenChange={open => {
						if (!open) onClose();
					}}
				>
					<SheetContent
						side="right"
						hideClose
						aria-describedby={undefined}
						className="z-[5040] flex w-full flex-col gap-0 bg-grey-1 p-0 sm:max-w-[560px]"
					>
						<div className="flex items-center gap-3 border-b border-grey-4 bg-background px-5 py-3">
							<SheetTitle className="min-w-0 flex-1 truncate text-headline-xs text-grey-13">
								{T('prospectingTools.tab.lookup')}
							</SheetTitle>
							<SheetClose className="shrink-0 rounded-md p-2 text-grey-10 hover:bg-grey-2">
								<X className="h-4 w-4" />
								<span className="sr-only">{T('default.close')}</span>
							</SheetClose>
						</div>
						<LookupBody query={query} onLookUp={onLookUp} onClose={onClose} />
					</SheetContent>
				</Sheet>
			</ShadcnScope>
		</ErrorBoundary>,
		document.body
	);

export default SupersearchLookupPanel;
